/**
 * Kickard PHASE 4.0.4 — Ranked Search screen.
 *
 * Flow: join → poll status → matched → hand off to OnlineGameScreen.
 * Cancels the ticket on explicit Cancel and (best-effort) on unmount.
 * Prevents double-join via a mounted-guard + busy flag.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadGuest } from '@/lib/guestIdentity';
import { saveOnlineSession } from '@/net/onlineSession';
import {
  joinRankedQueue, getRankedQueueStatus, cancelRankedQueue,
} from '@/api/rankedClient';

const POLL_MS = 800;

export default function RankedSearchScreen() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('idle'); // idle | waiting | matched | error | disabled | no_season
  const [error, setError] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // PHASE 4.1.2C — mark once we've navigated so an unmount cleanup
  // doesn't fire cancelRankedQueue against an already-matched ticket.
  // Persists across React 18 StrictMode double-mount unmounts, which
  // is exactly the behaviour we want here.
  const matchedRef = useRef(false);

  useEffect(() => {
    // Per-effect-invocation cancel flag. Local (NOT a ref) so a
    // StrictMode double-mount cleanup ONLY cancels the previous
    // effect run, not the next one. This is the fix for the
    // production ranked-queue hang: previously `cancelledRef` (a
    // ref) survived across mounts and short-circuited the /join
    // response handler on the SECOND mount, leaving both clients
    // frozen on "Searching…".
    let cancelled = false;
    const guest = loadGuest();
    if (!guest?.guestId) {
      setPhase('error'); setError('no_guest'); return;
    }
    (async () => {
      const r = await joinRankedQueue({
        guestId: guest.guestId,
        displayName: guest.displayName,
        profileSlug: guest.profileSlug,
      });
      if (cancelled) return;
      if (!r.ok) {
        if (r.code === 'ranked_disabled') setPhase('disabled');
        else if (r.code === 'no_active_season') setPhase('no_season');
        else { setPhase('error'); setError(r.code || 'error'); }
        return;
      }
      setTicket(r.ticketId);
      if (r.status === 'matched') {
        _navigateToMatch(r, guest);
      } else {
        setPhase('waiting');
      }
    })();
    return () => {
      cancelled = true;
      // Only cancel the ticket on real navigation away — NOT if we
      // already navigated into the matched room (that's tracked by
      // `matchedRef`). The server's ranked join/status is idempotent,
      // so a StrictMode double-mount that races cancel and re-join
      // simply produces one clean waiting entry.
      if (matchedRef.current) return;
      const g = loadGuest();
      if (g?.guestId) { cancelRankedQueue(g.guestId).catch(() => {}); }
    };
  }, []);

  useEffect(() => {
    if (phase !== 'waiting') return;
    const guest = loadGuest();
    if (!guest?.guestId) return;
    const t0 = Date.now();
    let cancelled = false;
    const tick = setInterval(async () => {
      setElapsed(Math.round((Date.now() - t0) / 1000));
      const s = await getRankedQueueStatus(guest.guestId);
      if (cancelled) return;
      if (!s.ok) {
        if (s.code === 'ranked_disabled') { setPhase('disabled'); clearInterval(tick); }
        else if (s.code === 'no_active_season') { setPhase('no_season'); clearInterval(tick); }
        return;
      }
      if (s.status === 'matched') {
        clearInterval(tick);
        _navigateToMatch(s, guest);
      } else if (s.status === 'cancelled' || s.status === 'expired' || s.status === 'idle') {
        clearInterval(tick);
        navigate('/ranked', { replace: true });
      }
    }, POLL_MS);
    return () => { cancelled = true; clearInterval(tick); };
  }, [phase]);

  function _navigateToMatch(match, guest) {
    // Idempotent — StrictMode double-mount may cause this to fire
    // once from each mount; the saveOnlineSession + navigate calls
    // are both safe under a second invocation.
    if (matchedRef.current) return;
    matchedRef.current = true;
    saveOnlineSession({
      roomCode: match.roomCode,
      playerId: match.playerId,
      token: match.token,
      name: guest.displayName,
      opponentName: match.opponentName,
    });
    navigate('/online', { replace: true });
  }

  function _cancel() {
    const g = loadGuest();
    if (g?.guestId) cancelRankedQueue(g.guestId).catch(() => {});
    navigate('/ranked', { replace: true });
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 k-safe-top"
         data-testid="ranked-search">
      <div className="max-w-sm w-full text-center space-y-5">
        <h1 className="text-2xl font-semibold">
          {phase === 'disabled' ? 'Ranked unavailable'
            : phase === 'no_season' ? 'No active season'
            : phase === 'error' ? 'Something went wrong'
            : phase === 'matched' ? 'Match found!'
            : 'Searching for ranked opponent…'}
        </h1>
        {phase === 'waiting' && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full border-2 border-amber-400 border-t-transparent animate-spin"
                 aria-label="searching" data-testid="ranked-search-spinner" />
            <div className="text-white/50 text-sm">Elapsed: {elapsed}s</div>
            <p className="text-white/40 text-xs">Preseason ratings may reset before Season 1.</p>
            <p className="text-white/40 text-xs">No direct rematch in Ranked.</p>
          </>
        )}
        {phase === 'error' && (
          <p className="text-red-300 text-sm" data-testid="ranked-search-error">
            {error || 'Please try again.'}
          </p>
        )}
        <button onClick={_cancel}
                className="k-back-btn mx-auto border border-white/20 hover:bg-white/[0.05]"
                data-testid="ranked-search-cancel-btn"
                aria-label="Cancel">Cancel</button>
      </div>
    </div>
  );
}
